import mapboxgl from "mapbox-gl";
import type { PlaceGeoJSON } from "./api";

/**
 * Mapbox source and layer IDs for places.
 */
export const PLACES_SOURCE_ID = "places";
export const PLACES_LAYER_ID = "places-layer";
export const ADVENTURE_MAP_PLACES_SOURCE_ID = "adventure-map-places";
export const ADVENTURE_MAP_PLACES_LAYER_ID = "adventure-map-places-layer";
export const SELECTED_PLACE_SOURCE_ID = "selected-place";
export const SELECTED_PLACE_LAYER_ID = "selected-place-layer";
export const RELATED_PLACES_SOURCE_ID = "related-places";
export const RELATED_PLACES_LAYER_ID = "related-places-layer";

/**
 * Default map center coordinates (Bulgaria - Bansko area).
 */
export const DEFAULT_MAP_CENTER: [number, number] = [23.5, 42.0];

/**
 * Map zoom and animation settings.
 */
export const MAP_MAX_ZOOM = 14;
export const FLY_TO_ZOOM = 14;
export const LANDING_PAGE_FLY_TO_ZOOM = 16;

/** Default flyTo options with capped duration */
export const FLY_TO_OPTIONS = {
  duration: 1000,
  maxDuration: 1000,
} as const;

/**
 * Fit bounds padding for different screen sizes.
 */
export const FIT_BOUNDS_PADDING = {
  desktop: 150,
  desktopWithPanel: { top: 80, bottom: 80, left: 500, right: 120 },
  mobile: { top: 80, bottom: 200, left: 60, right: 60 },
} as const;

export type FitBoundsPadding = number | { top: number; bottom: number; left: number; right: number };

/**
 * Empty GeoJSON FeatureCollection for initializing place stores.
 */
export const emptyPlacesGeoJSON: PlaceGeoJSON = {
  type: "FeatureCollection",
  attribution: "",
  features: [],
};

/**
 * Base layout shared between places and adventure map places.
 */
const basePlaceLayout: mapboxgl.SymbolLayerSpecification["layout"] = {
  "icon-anchor": "bottom",
  "icon-size": 0.8,
  "icon-allow-overlap": ["step", ["zoom"], false, 11, true],
  "text-field": ["get", "name"],
  "text-font": ["DIN Pro Medium", "Arial Unicode MS Regular"],
  "text-anchor": "top",
  "text-size": 14,
  "text-line-height": 1.2,
  "text-max-width": 10,
  "text-optional": true,
};

export const placePaint: mapboxgl.SymbolLayerSpecification["paint"] = {
  "text-color": "#333",
  "text-halo-color": "#fff",
  "text-halo-width": 1.5,
};

/**
 * Layout for places (white icons).
 */
export const placesLayout: mapboxgl.SymbolLayerSpecification["layout"] = {
  ...basePlaceLayout,
  "icon-image": ["concat", "poi-", ["get", "type"]],
};

/**
 * Layout for adventure map places (highlighted icons).
 */
export const adventureMapPlacesLayout: mapboxgl.SymbolLayerSpecification["layout"] = {
  ...basePlaceLayout,
  "icon-image": ["concat", "poi-", ["get", "type"], "-highlighted"],
  "icon-allow-overlap": true,
};

/**
 * Layout for selected place (selected icons).
 */
export const selectedPlaceLayout: mapboxgl.SymbolLayerSpecification["layout"] = {
  ...basePlaceLayout,
  "icon-image": ["concat", "poi-", ["coalesce", ["get", "type"], "unknown"], "-selected"],
  "icon-allow-overlap": true,
};

/**
 * Layout for related places (smaller highlighted icons, no labels).
 */
export const relatedPlacesLayout: mapboxgl.SymbolLayerSpecification["layout"] = {
  "icon-image": ["concat", "poi-", ["get", "type"], "-highlighted"],
  "icon-anchor": "bottom",
  "icon-size": 0.6,
  "icon-allow-overlap": true,
};

/**
 * Sets up long-press on touch devices to fire contextmenu events on the map.
 * This allows using the same contextmenu handler for both desktop (right-click)
 * and mobile (long-press).
 *
 * Also prevents touch events from immediately firing click events, allowing
 * long-press to work properly without triggering a click.
 */
export function setupMapTouchContextMenu(map: mapboxgl.Map, touchMoveTolerance = 4): () => void {
  const canvas = map.getCanvas();

  // Prevent native browser contextmenu on mobile (we handle it via long-press)
  const preventNativeContextMenu = (e: MouseEvent) => {
    e.preventDefault();
  };
  canvas.addEventListener("contextmenu", preventNativeContextMenu);

  const handleLongPress = (e: LongPressEvent) => {
    // Note: Don't call e.preventDefault() here - it causes the long-press-event
    // package to suppress the next click, which breaks dismissing the popup.
    // We handle click suppression ourselves by removing the touchend listener.
    const { clientX, clientY } = e.detail;
    const rect = canvas.getBoundingClientRect();
    const point = new mapboxgl.Point(clientX - rect.left, clientY - rect.top);
    const lngLat = map.unproject(point);

    map.fire("contextmenu", {
      lngLat,
      point,
      originalEvent: new MouseEvent("contextmenu", e.detail),
      preventDefault: () => {},
      defaultPrevented: false,
    } as mapboxgl.MapMouseEvent);
  };

  const handleTouchStart = (event: TouchEvent) => {
    event.preventDefault();
    if (!event.touches.length) return;

    const touch = event.touches[0];

    let handleTouchMove: (ev: TouchEvent) => void;
    let handleLongPressCleanup: (ev: LongPressEvent) => void;

    const handleTouchEnd = () => {
      canvas.removeEventListener("touchend", handleTouchEnd);
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("long-press", handleLongPressCleanup);

      const eventOptions: MouseEventInit = {
        screenX: touch.screenX,
        screenY: touch.screenY,
        clientX: touch.clientX,
        clientY: touch.clientY,
        bubbles: true,
        cancelable: true,
        view: window,
      };

      canvas.dispatchEvent(new MouseEvent("mousedown", eventOptions));
      canvas.dispatchEvent(new MouseEvent("mousemove", eventOptions));
      canvas.dispatchEvent(new MouseEvent("mouseup", eventOptions));
      canvas.dispatchEvent(new MouseEvent("click", eventOptions));
    };

    handleTouchMove = (touchMoveEvent) => {
      if (touchMoveEvent.touches.length) {
        const moveTouch = touchMoveEvent.touches[0];
        const distance = Math.sqrt((moveTouch.clientX - touch.clientX) ** 2 + (moveTouch.clientY - touch.clientY) ** 2);

        if (distance <= touchMoveTolerance) return;
      }

      canvas.removeEventListener("touchend", handleTouchEnd);
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("long-press", handleLongPressCleanup);
    };

    handleLongPressCleanup = () => {
      canvas.removeEventListener("touchend", handleTouchEnd);
      canvas.removeEventListener("touchmove", handleTouchMove);
      canvas.removeEventListener("long-press", handleLongPressCleanup);
    };

    canvas.addEventListener("long-press", handleLongPressCleanup);
    canvas.addEventListener("touchend", handleTouchEnd);
    canvas.addEventListener("touchmove", handleTouchMove);
  };

  canvas.addEventListener("long-press", handleLongPress);
  canvas.addEventListener("touchstart", handleTouchStart);

  // Return cleanup function
  return () => {
    canvas.removeEventListener("contextmenu", preventNativeContextMenu);
    canvas.removeEventListener("long-press", handleLongPress);
    canvas.removeEventListener("touchstart", handleTouchStart);
  };
}
